import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Target, Zap, Users, Trophy, Brain, Crown, ArrowRight, Check } from 'lucide-react';
import { Button } from '../components/Button';
import { Logo } from '../components/Logo';
const features = [{
  icon: Target,
  title: 'Smart Quest Management',
  description: 'Transform your goals into actionable quests with AI-powered suggestions and intelligent categorization.',
  color: 'from-blue-500 to-cyan-500'
}, {
  icon: Brain,
  title: 'AI-Powered Insights',
  description: 'Get personalized recommendations, motivational messages, and quest analysis powered by Gemini AI.',
  color: 'from-purple-500 to-pink-500'
}, {
  icon: Zap,
  title: 'Focus Mode',
  description: 'Eliminate distractions with Pomodoro timers, custom focus sessions, and productivity tracking.',
  color: 'from-orange-500 to-red-500'
}, {
  icon: Users,
  title: 'Collaborative Quests',
  description: 'Invite friends, share quests, and achieve goals together with real-time chat and progress tracking.',
  color: 'from-green-500 to-teal-500'
}, {
  icon: Trophy,
  title: 'Gamification & Rewards',
  description: 'Earn XP, level up, unlock achievements, and compete on leaderboards to stay motivated.',
  color: 'from-yellow-500 to-orange-500'
}, {
  icon: Crown,
  title: 'Premium Features',
  description: 'Unlimited quests, advanced analytics, data export, custom themes, and priority support.',
  color: 'from-indigo-500 to-purple-500'
}];
const benefits = ['Unlimited quest creation', 'AI-powered suggestions', 'Real-time collaboration', 'Advanced focus modes', 'Progress analytics', 'Achievement system', 'Leaderboard rankings', 'Dark mode support', 'Mobile responsive', 'Data export (Premium)', 'Custom categories (Premium)', 'Priority support (Premium)'];
export function OnboardingPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const handleGetStarted = () => {
    navigate('/');
  };
  return <div className="min-h-screen bg-gradient-to-br from-game-bg-pink via-white to-game-bg-yellow dark:from-[#1a1d29] dark:via-[#252936] dark:to-[#1a1d29]" data-id="element-1048">
      {/* Header */}
      <header className="px-6 py-6" data-id="element-1049">
        <div className="max-w-7xl mx-auto" data-id="element-1050">
          <Logo data-id="element-1051" />
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-6 py-12 lg:py-20" data-id="element-1052">
        <div className="max-w-7xl mx-auto text-center" data-id="element-1053">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1054">
            <h1 className="text-4xl lg:text-6xl font-fredoka font-bold text-game-dark dark:text-white mb-6" data-id="element-1055">
              Turn Goals Into
              <span className="block bg-gradient-to-r from-game-purple via-game-teal to-game-coral bg-clip-text text-transparent" data-id="element-1056">
                Focused Quests
              </span>
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 font-quicksand mb-8 max-w-2xl mx-auto" data-id="element-1057">
              AI-powered productivity platform that transforms your goals into
              actionable quests with gamification, collaboration, and
              intelligent insights.
            </p>
            <Button size="lg" onClick={handleGetStarted} icon={<ArrowRight className="w-5 h-5" data-id="element-1059" />} className="bg-gradient-to-r from-game-purple to-game-teal" data-id="element-1058">
              Get Started Free
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="px-6 py-12" data-id="element-1060">
        <div className="max-w-7xl mx-auto" data-id="element-1061">
          <h2 className="text-3xl font-fredoka font-bold text-center text-game-dark dark:text-white mb-12" data-id="element-1062">
            Everything You Need to Succeed
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1063">
            {features.map((feature, index) => {
            const Icon = feature.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.1
            }} className="bg-white dark:bg-[#252936] rounded-3xl p-6 shadow-lg hover:shadow-xl transition-shadow" data-id="element-1064">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4`} data-id="element-1065">
                    <Icon className="w-7 h-7 text-white" data-id="element-1066" />
                  </div>
                  <h3 className="text-xl font-fredoka font-bold text-game-dark dark:text-white mb-2" data-id="element-1067">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 font-quicksand" data-id="element-1068">
                    {feature.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits List */}
      <section className="px-6 py-12 bg-white/50 dark:bg-[#252936]/50" data-id="element-1069">
        <div className="max-w-4xl mx-auto" data-id="element-1070">
          <h2 className="text-3xl font-fredoka font-bold text-center text-game-dark dark:text-white mb-12" data-id="element-1071">
            What You Get
          </h2>
          <div className="grid md:grid-cols-2 gap-4" data-id="element-1072">
            {benefits.map((benefit, index) => <motion.div key={index} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: index * 0.05
          }} className="flex items-center gap-3" data-id="element-1073">
                <div className="w-6 h-6 bg-game-teal rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1074">
                  <Check className="w-4 h-4 text-white" data-id="element-1075" />
                </div>
                <span className="font-quicksand text-gray-700 dark:text-gray-300" data-id="element-1076">
                  {benefit}
                </span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 py-12 lg:py-20" data-id="element-1077">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1078">
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.5
        }} className="bg-gradient-to-br from-game-purple via-game-teal to-game-coral p-8 lg:p-12 rounded-3xl text-white" data-id="element-1079">
            <h2 className="text-3xl lg:text-4xl font-fredoka font-bold mb-4" data-id="element-1080">
              Ready to Transform Your Productivity?
            </h2>
            <p className="text-lg opacity-90 font-quicksand mb-8" data-id="element-1081">
              Join thousands of users who are achieving their goals with
              QuestZen AI
            </p>
            <Button size="lg" onClick={handleGetStarted} className="bg-white text-game-purple hover:bg-gray-100" icon={<ArrowRight className="w-5 h-5" data-id="element-1083" />} data-id="element-1082">
              Start Your Journey
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 py-8 border-t border-gray-200 dark:border-gray-800" data-id="element-1084">
        <div className="max-w-7xl mx-auto text-center" data-id="element-1085">
          <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1086">
            © 2024 QuestZen AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>;
}